import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/apiClient';

import {
  type ClassificationDraft,
  type CurationFilters,
  type CurationItem,
  type Locale,
  type NewExercise,
  type PublicationStatus,
  type TranslationDraft,
  clearMedia,
  createExercise,
  getForCuration,
  getMediaLimits,
  listForCuration,
  saveClassification,
  saveTranslation,
  setPublication,
  uploadMedia,
} from './api';

export const CURATION_KEY = ['curation', 'exercises'] as const;

const retry = (failureCount: number, error: unknown) => {
  if (error instanceof ApiError && error.status < 500) return false;
  return failureCount < 2;
};

export function useCurationQueue(filters: CurationFilters) {
  return useQuery({
    queryKey: [...CURATION_KEY, filters],
    queryFn: () => listForCuration(filters),
    retry,
    // A fila é longa e a pessoa alterna entre filtros o tempo todo. Manter o
    // resultado anterior enquanto o novo chega evita a tela piscar vazia a cada
    // troca — que numa lista de centenas de itens é desnorteante.
    placeholderData: (anterior) => anterior,
  });
}

export function useCurationItem(exerciseId: string) {
  return useQuery({
    queryKey: [...CURATION_KEY, 'item', exerciseId],
    queryFn: () => getForCuration(exerciseId),
    retry,
  });
}

/**
 * Toda escrita devolve o item inteiro, e o cache é atualizado com ele.
 *
 * Isso importa por causa de `blockers`: a lista de pendências muda a cada
 * gravação, e é ela que habilita o botão de publicar. Invalidar e esperar o
 * refetch deixaria o botão errado por alguns instantes — tempo suficiente para
 * a pessoa clicar e levar um 422 que não faz sentido para ela.
 */
function useCurationMutation<TVars>(
  fn: (vars: TVars) => Promise<CurationItem>,
): ReturnType<typeof useMutation<CurationItem, Error, TVars>> {
  const queryClient = useQueryClient();
  return useMutation<CurationItem, Error, TVars>({
    mutationFn: fn,
    onSuccess: (item) => {
      // O item volta inteiro e entra no cache direto — sem esperar refetch.
      queryClient.setQueryData([...CURATION_KEY, 'item', item.exercise_id], item);
      // A fila é recarregada, porque a gravação pode ter tirado o item do
      // filtro atual (escrever o português o remove de `missing_locale=pt-BR`).
      // O `predicate` poupa o item que acabou de ser posto no cache.
      void queryClient.invalidateQueries({
        queryKey: CURATION_KEY,
        predicate: (consulta) => consulta.queryKey[2] !== 'item',
      });
    },
  });
}

export function useSaveTranslation() {
  return useCurationMutation(
    ({
      exerciseId,
      locale,
      draft,
    }: {
      exerciseId: string;
      locale: Locale;
      draft: TranslationDraft;
    }) => saveTranslation(exerciseId, locale, draft),
  );
}

export function useSaveClassification() {
  return useCurationMutation(
    ({ exerciseId, draft }: { exerciseId: string; draft: ClassificationDraft }) =>
      saveClassification(exerciseId, draft),
  );
}

export function useCreateExercise() {
  return useCurationMutation((draft: NewExercise) => createExercise(draft));
}

export function useUploadMedia() {
  return useCurationMutation(
    ({ exerciseId, file, isGenerated }: { exerciseId: string; file: File; isGenerated: boolean }) =>
      uploadMedia(exerciseId, file, isGenerated),
  );
}

export function useClearMedia() {
  return useCurationMutation(({ exerciseId }: { exerciseId: string }) => clearMedia(exerciseId));
}

export function useMediaLimits() {
  return useQuery({
    queryKey: [...CURATION_KEY, 'media-limits'],
    queryFn: getMediaLimits,
    retry,
    // Não muda entre telas: são constantes do servidor.
    staleTime: Infinity,
  });
}

export function useSetPublication() {
  return useCurationMutation(
    ({ exerciseId, status }: { exerciseId: string; status: PublicationStatus }) =>
      setPublication(exerciseId, status),
  );
}

/**
 * As pendências que o servidor devolveu na recusa de publicação.
 *
 * Chegam em `details.blockers` porque a tela precisa listar todas de uma vez —
 * mostrar uma por vez transformaria a curadoria em tentativa e erro.
 */
export function publicationBlockers(error: unknown): string[] {
  if (error instanceof ApiError && Array.isArray(error.details.blockers)) {
    return error.details.blockers.filter((item): item is string => typeof item === 'string');
  }
  return [];
}

export function curationErrorKey(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'forbidden') return 'curation:error.forbidden';
    const rule = error.details.rule;
    if (typeof rule === 'string') return `curation:rule.${rule}`;
  }
  return 'curation:error.unknown';
}
